import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Badge } from "primereact/badge";
import { ProgressSpinner } from "primereact/progressspinner";
import { useDispatch, useSelector } from "react-redux";
import { 
  closeProviderDialog, 
  addProviderToCart, 
  removeProviderFromCart,
  resetServiceBooking,
  closeDialogForLogin,
  setShouldProcessCartAfterLogin,
  openServiceDialog
} from "../../../store/serviceBookingSlice";
import { useNavigate } from "react-router-dom";
import styles from './ProviderSelectionDialog.module.css';
import PriestProviderCard from './PriestProviderCard';
import CateringProviderCard from'./CateringProviderCard';
import TentProviderCard from './TentProviderCard'


import {   getPriestsListByLocation,
  getTentHouseListByLocationpost,
  getCateringListByLocation,getCateringItemMasterList } from "../../../Apiservices/Auth/servers/fetchProviderData";
const ProviderSelectionDialog = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const { 
    isProviderDialogOpen, 
    serviceType, 
    selectedProviders, 
    commonData,
    priestEventName 
  } = useSelector(state => state.serviceBooking);
  
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
      if (isProviderDialogOpen && serviceType) {
    // Set flag to prevent auto-processing while in dialog
    localStorage.setItem('inProviderDialog', 'true');
    fetchProviders();
  } else {
    // Clear flag when dialog closes
    localStorage.removeItem('inProviderDialog');
  }
  }, [isProviderDialogOpen, serviceType]);

  // Simple back function in ProviderSelectionDialog
const handleBackToSelection = () => {
  
  
  // Close provider dialog
  dispatch(closeProviderDialog());
    // Save current state in case user needs to login again
    const pendingServiceData = {
    commonData,
    serviceType: serviceType, // Use the actual serviceType, not hardcoded
    currentStep: serviceType === 'catering' ? 'cateringSelection' : 'tentItems'
  };
  localStorage.setItem('pendingServiceData', JSON.stringify(pendingServiceData));
  // Reopen service dialog after a small delay to ensure state is updated
  setTimeout(() => {
    dispatch(openServiceDialog());
  }, 50);
};
const getBackButtonLabel = () => {
  switch (serviceType) {
    case 'tent': return 'Back to Tent Items';
    case 'catering': return 'Back to Menu Selection';
    default: return 'Back';
  }
};
 const fetchProviders = async () => {
  setLoading(true);
  setError(null);
  
  try {
    let response;
    const { latitude, longitude, eventId, tentServiceIds } = commonData;

    

    switch (serviceType) {
      case 'catering':
         const cateringData = commonData.cateringData;
          const mealIds = cateringData?.meal ? [cateringData.meal.meal_id] : [];

          // Extract all item IDs from included_items and included_side_items
          const allItemIds = [
            ...(cateringData?.included_items || []).map(item => item.itemId || item.id),
            ...(cateringData?.included_side_items || []).map(item => item.itemId || item.id)
          ].filter(Boolean); // Remove any null/undefined values
        
          
        
          response = await getCateringListByLocation(latitude, longitude, eventId, {
            mealIds: mealIds,
            items: allItemIds,
            quantity: commonData.cateringQuantity || 10 // Default to 10 if not specified
          });
          break;                  
      case 'tent':
        // Pass the selected service IDs for tent house
        response = await getTentHouseListByLocationpost(
          latitude, 
          longitude, 
          eventId,
          tentServiceIds || [] // Pass the selected service IDs
        );
        break;
      case 'priest':
        response = await getPriestsListByLocation(latitude, longitude, eventId);
        break;
      default:
        throw new Error('Invalid service type');
    }

    
    
    const transformedProviders = transformProviderData(response, serviceType);
    setProviders(transformedProviders);
    
  } catch (err) {
    console.error('Error fetching providers:', err);
    setError('Failed to load providers. Please try again.');
    setProviders([]);
  } finally {
    setLoading(false);
  }
};

// Update handleAddToCart to include login check and centralize all validation
const handleAddToCart = (provider) => {
  

  // Check if user is logged in
  if (!isLoggedIn) {
    // Save state and redirect to login
    const pendingServiceData = {
      commonData,
      serviceType,
      currentStep: 'providerSelection',
      attemptedProvider: provider, // Save which provider user tried to add
      selectedProviders // Save current selected providers
    };
    localStorage.setItem('pendingServiceData', JSON.stringify(pendingServiceData));
    
    dispatch(closeDialogForLogin());
    navigate("/login");
    return;
  }

  // Validation checks (only if logged in)
  const isPriestInCart = selectedProviders.some(p => p.serviceType === 'priest');
  if (serviceType === 'priest' && isPriestInCart) {
    alert("Only one priest can be added per event. Please remove the existing priest first.");
    return;
  }
  
  // Check if tent provider from same vendor is already in cart
  const isSameTentVendorInCart = selectedProviders.some(
    p => p.serviceType === 'tent' && p.id === provider.id
  );
  
  if (serviceType === 'tent' && isSameTentVendorInCart) {
    alert("This tent provider is already in your cart. You can only select one tent provider per event.");
    return;
  }

  // For tent service, add mappingId to selected items
  let enhancedSelectedTentItems = [];
  if (serviceType === 'tent' && commonData.selectedTentItems) {
    enhancedSelectedTentItems = commonData.selectedTentItems.map(item => {
      // Find the corresponding service in provider to get mappingId
      const service = provider.services?.find(s => s.serviceId === item.id);
      return {
        ...item,
        mapping_id: service?.mappingId // Add mapping_id to each item
      };
    });
  }
  
  const cartItem = {
    ...provider,
    serviceType,
    eventDate: commonData.eventDate,
    location: commonData.address,
    latitude: commonData.latitude,
    longitude: commonData.longitude,
    whoseEvent: commonData.whoseEvent,
    // Include tent items if service is tent
    ...(serviceType === 'tent' && { 
      selectedItems: enhancedSelectedTentItems, // Use enhanced items with mapping_id
      selectedTentItems: enhancedSelectedTentItems, // Keep both for consistency
      tentServiceIds: commonData.tentServiceIds
    }),
    // Include priest event name if applicable
    ...(serviceType === 'priest' && { eventName: priestEventName })
  };
  
  
  dispatch(addProviderToCart(cartItem));
};

  // Transform API response to consistent format
const transformProviderData = (apiResponse, serviceType) => {
  if (!apiResponse || !apiResponse.data) return [];
  
  return apiResponse.data.map(provider => {
    // Common fields for all service types
    const baseProvider = {
      id: provider.providerId || provider.id, // Use providerId from API
      // Include original API data for reference
      originalData: provider,
      // Default quantity for services that need it
      quantity: 1
    };

    // Service-specific transformations
    switch (serviceType) {
      case 'priest':
        return {
          ...baseProvider,
          name: `${provider.firstName} ${provider.lastName || ''}`.trim(),
          firstName: provider.firstName,
          lastName: provider.lastName,
          rating: provider.rating || 0,
          price: provider.price || 2000,
          image: provider.profileImage,
          description: provider.description || `Experienced priest specializing in ${provider.events?.[0]?.name || 'various'} ceremonies`,
          distanceInMeter: provider.distanceInMeter,
          locality: provider.locality,
          city: provider.city,
          state: provider.state,
          experience: provider.experience,
          languages: provider.languages,
          events: provider.events
        };

      case 'catering':
        return {
          ...baseProvider,
          name: provider.businessName || provider.name || provider.providerName,
          rating: provider.rating || provider.averageRating || 0,
          price: formatPrice(provider.price, provider.rate, serviceType),
          image: provider.imageUrl,
          description: provider.description || provider.serviceDescription || 'Professional catering service',
          minOrder: provider.minOrder,
          capacity: provider.capacity,
          cuisineType: provider.cuisineType
        };

      case 'tent':
        // Get the first service to show details
        const firstService = provider.services?.[0];
        return {
          ...baseProvider,
          name: provider.name || provider.businessName || 'Tent House Provider',
          rating: provider.rating || 0,
          price: firstService?.price || provider.price || 5000,
          image: provider.imageUrl,
          description: firstService?.description || provider.description || 'Professional tent house service',
          distanceInMeter: provider.distanceInMeter,
          locality: provider.locality,
          city: provider.city,
          state: provider.state,
          formattedAddress: provider.formattedAddress,
          // Service details
          serviceName: firstService?.serviceName,
          serviceDescription: firstService?.description,
          discountPrice: firstService?.discountPrice,
          services: provider.services, // Keep all services array
          // For backward compatibility
          tentSize: provider.tentSize,
          capacity: provider.capacity,
          amenities: provider.amenities
        };

      default:
        return {
          ...baseProvider,
          name: provider.businessName || provider.name || provider.providerName || 'Service Provider',
          rating: provider.rating || provider.averageRating || 0,
          price: formatPrice(provider.price, provider.rate, serviceType),
          image: provider.imageUrl || provider.profileImage,
          description: provider.description || provider.serviceDescription || 'Professional service provider'
        };
    }
  });
};

  const formatPrice = (price, rate, serviceType) => {
    if (price) return `₹${price}`;
    if (rate) return `₹${rate}`;
    
    // Default pricing based on service type
    switch (serviceType) {
      case 'catering': return '₹150 per plate';
      case 'tent': return '₹5000 per day';
      case 'priest': return '₹2000 per ceremony';
      default: return 'Price on request';
    }
  };


  const handleRemoveFromCart = (providerId) => {
    dispatch(removeProviderFromCart(providerId));
  };

  const handleQuantityChange = (providerId, newQuantity) => {
    if (newQuantity < 1) return;
    
    // Update quantity in providers list for display
    setProviders(prev => prev.map(provider => 
      provider.id === providerId 
        ? { ...provider, quantity: newQuantity }
        : provider
    ));

    // Also update in cart if provider is already in cart
    const providerInCart = selectedProviders.find(p => p.id === providerId);
    if (providerInCart) {
      dispatch(removeProviderFromCart(providerId));
      dispatch(addProviderToCart({
        ...providerInCart,
        quantity: newQuantity
      }));
    }
  };

  const isProviderInCart = (providerId) => {
    return selectedProviders.some(provider => provider.id === providerId);
  };

  const getProviderQuantity = (providerId) => {
    const providerInCart = selectedProviders.find(p => p.id === providerId);
    return providerInCart?.quantity || 1;
  };

const handleProceedToLogin = () => {
  // Check if user is already logged in
  if (isLoggedIn) {
    // User is logged in - close dialog, cart items will be processed by useLoginCartHandler
    if (selectedProviders.length > 0) {
      // Set flag to show EventTabs after cart processing
      
      dispatch(setShouldProcessCartAfterLogin(true));
      localStorage.setItem('showEventTabsAfterLogin', 'true');
      
      // Save cart data for processing after dialog closes
      const pendingCartData = {
        commonData,
        selectedProviders,
        serviceType
      };
      localStorage.setItem('pendingCartData', JSON.stringify(pendingCartData));
    }
    
    // Close the dialog
    dispatch(closeDialogForLogin());
    
  } else {
    // User is not logged in - redirect to login
    if (selectedProviders.length > 0) {
      localStorage.setItem('showEventTabsAfterLogin', 'true');
    }
    
    // Save all current state for resume after login
    const pendingServiceData = {
      commonData,
      serviceType,
      currentStep: 'providerSelection',
      selectedProviders // Save all selected providers
    };
    localStorage.setItem('pendingServiceData', JSON.stringify(pendingServiceData));
    
    dispatch(closeDialogForLogin());
    navigate("/login");
  }
};

  const handleClose = () => {
    dispatch(setShouldProcessCartAfterLogin(false));

    dispatch(closeProviderDialog());
    dispatch(resetServiceBooking());
  };

  const getDialogHeader = () => {
    switch (serviceType) {
      case 'catering': return 'Select Catering Service - Step 2 of 2';
      case 'tent': return 'Select Tent House - Step 2 of 2';
      case 'priest': return 'Select Priest - Step 2 of 2';
      default: return 'Select Provider';
    }
  };

    // Add the match type function
  const getItemMatchType = (item) => {
    const userSelectedItems = commonData.cateringSelectedItems?.map(item => item.name) || [];
    
    if (!userSelectedItems.length) return 'other';
    
    const itemIncludedItems = item.includedItems || [];
    const itemAllText = [
      item.itemName,
      item.meal?.name,
      item.type?.name,
      item.category?.name,
      ...itemIncludedItems.map(i => i.itemName || i.name)
    ].filter(Boolean).join(' ').toLowerCase();
    
    const userSelectedText = userSelectedItems.join(' ').toLowerCase();
    
    const hasAllSelectedItems = userSelectedItems.every(selectedItem => 
      itemAllText.includes(selectedItem.toLowerCase())
    );
    
    if (hasAllSelectedItems) return 'exact';
    
    const hasSomeSelectedItems = userSelectedItems.some(selectedItem => 
      itemAllText.includes(selectedItem.toLowerCase())
    );
    
    if (hasSomeSelectedItems) return 'partial';
    
    return 'other';
  };

  const footerContent = (
  <div className={styles.footer}>
    {/* <div className={styles.cartSummary}>
      <span>{selectedProviders.length} service(s) selected</span>
      <Badge value={selectedProviders.length} className={styles.cartBadge} />
      {selectedProviders.length > 0 && (
        <span className={styles.totalItems}>
          Total items: {selectedProviders.reduce((sum, item) => sum + (item.quantity || 1), 0)}
        </span>
      )}
    </div> */}
    <div className={styles.footerButtons}>
      {/* Add Back Button */}
      {(serviceType === 'priest' ||serviceType === 'tent' || serviceType === 'catering') && (
  <Button
    label={getBackButtonLabel()}
    icon="pi pi-arrow-left"
    className={styles.backButton}
    onClick={handleBackToSelection}
  />
)}
      {/* <Button
        label="Refresh Providers"
        icon="pi pi-refresh"
        className={styles.refreshButton}
        onClick={fetchProviders}
        disabled={loading}
      /> */}
       <Button
        label={isLoggedIn ? "Add to Cart" : "Proceed to Login"}
        icon={isLoggedIn ? "pi pi-shopping-cart" : "pi pi-user"}
        className={styles.loginButton}
        onClick={handleProceedToLogin}
        disabled={selectedProviders.length === 0}
      />
    </div>
  </div>
);
const getCateringSelectionInfo = () => {
  if (serviceType !== 'catering' || !commonData.cateringData) return null;
  
  const cateringData = commonData.cateringData;
  
  // Debug log to see the actual data structure
  
  
  return (
    <div className={styles.cateringSelectionInfo}>
      <h4>Your Menu Selection:</h4>
      <div className={styles.cateringDetails}>
        <div className={styles.singlerow}>
        <p><strong>Package:</strong> {cateringData.item_name || cateringData.custom_combo_name}</p>
        {cateringData.meal?.meal_name && (
          <p><strong>Meal:</strong> {cateringData.meal.meal_name}</p>
        )}
        </div>
        {/* Check for includedItems (new API structure) */}
        {/* {cateringData.includedItems && cateringData.includedItems.length > 0 && (
          <div className={styles.includedItems}>
            <strong>Included Items:</strong>
            <div className={styles.itemsList}>
              {cateringData.includedItems.map((item, index) => (
                <span key={index} className={styles.item}>
                  {item.itemName ||""}
                  {index < cateringData.includedItems.length - 1 && ', '}
                </span>
              ))}
            </div>
          </div>
        )} */}
        
        {/* Check for included_items (old structure) */}
        {cateringData.included_items && cateringData.included_items.length > 0 && (
          <div className={styles.includedItems}>
            <strong>Included Items:</strong>
            <div className={styles.itemsList}>
              {cateringData.included_items.map((item, index) => (
                <span key={index} className={styles.item}>
                  {item.itemName ||""}
                  {index < cateringData.included_items.length - 1 && ', '}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Check for side items in both structures */}
        {(cateringData.included_side_items && cateringData.included_side_items.length > 0) && (
          <div className={styles.sideItems}>
            <strong>Side Items:</strong>
            <div className={styles.itemsList}>
              {cateringData.included_side_items.map((item, index) => (
                <span key={index} className={styles.item}>
                  {item.itemName ||""}
                  {index < cateringData.included_side_items.length - 1 && ', '}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
    const renderProviderCard = (provider) => {
    const commonProps = {
      provider,
      isInCart: isProviderInCart(provider.id),
      onAddToCart: handleAddToCart,
      onRemoveFromCart: handleRemoveFromCart,
      serviceType,
       isLoggedIn,
    };

    switch (serviceType) {
      case 'priest':
        return (
          <PriestProviderCard
            key={provider.id}
            {...commonProps}
          />
        );
      case 'catering':
      return (
        <>
          {provider.originalData?.items?.map((item, index) => (
            <CateringProviderCard
              key={`${provider.id}-${item.itemId || index}`}
              provider={provider}
              item={item}
              isInCart={isProviderInCart(provider.id)}
              onAddToCart={handleAddToCart}  // Use handleAddToCart directly
              onRemoveFromCart={handleRemoveFromCart}  // Use handleRemoveFromCart directly
              userSelectedItems={commonData.cateringSelectedItems?.map(item => item.name) || []}
              quantity={getProviderQuantity(provider.id)}
              onQuantityChange={handleQuantityChange}
              matchType={getItemMatchType(item)}
              isLoggedIn={isLoggedIn}
            />
          ))}
        </>
      );
      case 'tent':
        return (
          <TentProviderCard
            key={provider.id}
            {...commonProps}
            quantity={getProviderQuantity(provider.id)}
            onQuantityChange={handleQuantityChange}
            isLoggedIn={isLoggedIn} 
          />
        );
      default:
        return null;
    }
  };

 return (
  <Dialog
    header={getDialogHeader()}
    visible={isProviderDialogOpen}
    onHide={handleClose}
    closable={true}
    breakpoints={{ "960px": "75vw", "640px": "100vw" }}
    className={styles.providerDialog}
    footer={footerContent}
  >
    <div className={styles.providerContainer}>
      <div className={styles.eventInfo}>
        {/* <h4>Event Details:</h4> */}
        <div className={styles.commondatainfo}>
        <p><strong>Venue:</strong> {commonData.address}</p>
        <p><strong>Date:</strong> {commonData.eventDate ? new Date(commonData.eventDate).toLocaleString() : 'Not set'}</p>
        <p><strong>For:</strong> {commonData.whoseEvent}</p>
        </div>
              {/* Show catering selection info */}
        {serviceType === 'catering' && getCateringSelectionInfo()}
        {serviceType === 'priest' && priestEventName && (
          <p><strong>Event:</strong> {priestEventName}</p>
        )}
      {serviceType === 'tent' && commonData.selectedTentItems && commonData.selectedTentItems.length > 0 && (
          <div className={styles.selectedItemsContainer}>
            <span>Selected Items</span>
            <div className={styles.selectedItemsScroll}>
              {commonData.selectedTentItems.map((item, index) => (
                <span key={item.id} className={styles.selectedItem}>
                  {item.service_name}
                  {item.count > 1 && ` (${item.count})`}
                  {index < commonData.selectedTentItems.length - 1 && ', '}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div className={styles.loadingContainer}>
          <ProgressSpinner />
          <p>Loading providers...</p>
        </div>
      )}

      {error && (
        <div className={styles.errorContainer}>
          <p className={styles.errorText}>{error}</p>
          <Button
            label="Try Again"
            icon="pi pi-refresh"
            onClick={fetchProviders}
          />
        </div>
      )}

      {!loading && !error && (
        <div className={styles.providersScrollContainer}>
          <div className={styles.providersGrid}>
             {providers.map(provider => renderProviderCard(provider))}
          </div>
        </div>
      )}
        {providers.length === 0 &&
        <h3>No providers available</h3>
        }
      {!loading && !error && providers.length === 0 && serviceType!="priest" && (
        <div className={styles.noProviders}>
          <p>No providers available for {serviceType} service in your area.</p>
          <Button
            label="Try Different Location"
            icon="pi pi-map-marker"
            onClick={handleBackToSelection}
          />
        </div>
      )}
    </div>
  </Dialog>
);
};

export default ProviderSelectionDialog;